import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { Box, Text } from '@machobear/design-system'

import { breakpoints } from '@/styles'
import isVerified from '@/utils/is-verified'
import Avatar from '@/components/avatar'
import BlurredText from '@/components/blurred-text'
import { showLandlordProfileModal } from '@/components/landlord-profile-modal'
import { Tooltip } from '@/components/tooltip'
import VerifiedBadge from '@/components/verified-badge'

const Landlord = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex: 1;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
    z-index: 11;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
  }
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 140px;
`

const DoubleTextWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
`

const AvatarWrapper = styled.div`
  position: relative;
  display: flex;
  height: 60px;
  width: 80px;
  margin-right: 16px;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  width: 100%;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    justify-content: flex-start;
  }
`

const NameWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const LandlordInfo = ({
  landlord,
  isLandlordInfoPresent,

  companyLink,
  hideCompanyInfo,
  ...rest
}) => {
  const { t } = useTranslation()

  const isLandlordVerified = landlord.identity_verified_state === 'verified'

  const idVerifiedText = t(
    'b.listing.view.id_verified.tooltip',
    'This user is ID / Business Licence verified.'
  )

  return (
    <Landlord {...rest}>
      <DoubleTextWrapper>
        <AvatarWrapper>
          <Avatar
            width={56}
            height={56}
            location={landlord.picture}
            shadow
            onClick={
              isLandlordInfoPresent
                ? () => showLandlordProfileModal({ landlord, t })
                : null
            }
            isBlurred={true}
          />
        </AvatarWrapper>

        <Column>
          <NameWrapper>
            <BlurredText variant={{ weight: 'large' }}>****</BlurredText>
            <Tooltip text={idVerifiedText} position="top">
              <VerifiedBadge
                verified={isVerified(landlord, landlord.company)}
                type="id"
                height={22}
                width={30}
                bottom={-2}
              />
            </Tooltip>
            {!isLandlordVerified && (
              <Box
                background="cautionLight"
                marginLeft="xxsmall"
                paddingX="xxsmall"
                paddingY="xxxsmall"
                borderRadius="small"
              >
                <Text weight="semiBold" size="small">
                  {t(
                    'listing:b.listing.preview.landlord.id_unverified.message',
                    'ID Unverified'
                  )}
                </Text>
              </Box>
            )}
          </NameWrapper>
          {!hideCompanyInfo && landlord.company.name && (
            <BlurredText variant={{ weight: 'medium' }}>
              **** **** **** ****
            </BlurredText>
          )}
          <Text size="xsmall" tone="secondary">
            Show phone number
          </Text>
        </Column>
      </DoubleTextWrapper>
      <ButtonWrapper>
        {isLandlordInfoPresent && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            background="disabled"
            borderRadius="standard"
            paddingY="xsmall"
            paddingX="xlarge"
          >
            <Text weight="strong" size="xsmall" tone="secondary">
              {t('listing:b.listing.view.read_bio.text', 'Read Bio')}
            </Text>
          </Box>
        )}
        {companyLink && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            background="disabled"
            borderRadius="standard"
            paddingY="xsmall"
            paddingX="xlarge"
          >
            <Text weight="strong" size="xsmall" tone="secondary">
              {t(
                'listing:b.listing.view.view_all_listings.link',
                'View listings'
              )}
            </Text>
          </Box>
        )}
      </ButtonWrapper>
    </Landlord>
  )
}

export default LandlordInfo
